import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSetupStatus from '../../hooks/useSetupStatus';
import safeStorage from '../../utils/safeStorage'; // 🔐 Import du contexte d'authentification
import { 
  Login as LogIn,
  Person as User,
  Lock,
  Visibility as Eye,
  VisibilityOff as EyeOff,
  Warning as AlertCircle,
  CheckCircle,
  Info,
  Security as Shield,
  ChevronRight,
  Description as FileText,
  BusinessCenter as Briefcase
} from '@mui/icons-material';

const TravelITLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth(); // 🔐 Utilisation du contexte

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null); // Track setup status from backend
  const [statusMessage, setStatusMessage] = useState(null); // Setup status message with type
  const [suggestedUsername, setSuggestedUsername] = useState(''); // Username from setup

  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loading, setLoading] = useState(false);

  // 🔐 Vérifier si déjà authentifié via le contexte
  useEffect(() => {
    if (isAuthenticated) {
      setLoginSuccess(true);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 800);
    }
  }, [isAuthenticated, navigate]);

  // Use centralized setup status hook (no more redundant API calls!)
  const { isSetupComplete } = useSetupStatus();
  
  // Update local state when setup status changes
  useEffect(() => {
    setSetupStatus(isSetupComplete);
  }, [isSetupComplete]);

  // Check for status message and username from setup/navigation
  useEffect(() => {
    // Check navigation state for setup success, warning, or info messages
    if (location.state?.message) {
      setStatusMessage({
        text: location.state.message,
        type: location.state.type || 'success' // default to success if no type specified
      });
      if (location.state.username) {
        setSuggestedUsername(location.state.username);
        setFormData(prev => ({ ...prev, username: location.state.username }));
      }
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    }
    
    // Check stored username from setup
    const storedUsername = safeStorage.getItem('setup_username');
    if (storedUsername) {
      setSuggestedUsername(storedUsername);
      setFormData(prev => ({ ...prev, username: storedUsername }));
      // Clear stored username after using it once
      safeStorage.removeItem('setup_username');
    }
  }, [location, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      
      // 🔐 UTILISER LA VRAIE FONCTION LOGIN DU CONTEXTE (ASYNC)
      const loginResult = await login(formData.username, formData.password);
      
      if (loginResult) {
        setLoginSuccess(true);
        console.log('✅ TravelIT: Login réussi via AuthContext');
      } else {
        throw new Error('Nom d\'utilisateur ou mot de passe incorrect');
      }
      
    } catch (error) {
      console.error('❌ TravelIT: Erreur login:', error);
      setErrors({ submit: error.message || 'Nom d\'utilisateur ou mot de passe incorrect' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  if (loginSuccess) {
    return (
      <div className="setup-wizard">
        <div className="setup-background" />
        <div className="gradient-overlay" />
        
        <div className="setup-content">
          <div className="setup-container">
            <div className="setup-card">
              <div className="setup-header">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="welcome-logo"
                  style={{ 
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    boxShadow: '0 0 30px rgba(16, 185, 129, 0.6)'  
                  }}
                >
                  <img 
                    src="./images/logo.png" 
                    alt="TravelIT Logo" 
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      objectFit: 'contain' 
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = document.createElement('div');
                      fallback.style.cssText = 'width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #fff;';
                      fallback.textContent = 'T';
                      e.target.parentNode.replaceChild(fallback, e.target);
                    }}
                  />
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="setup-title"
                  style={{ color: '#10b981' }}
                >
                  Connexion Réussie !
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="setup-subtitle"
                  style={{ fontSize: '1.2rem' }}
                >
                  Redirection vers le tableau de bord...
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '2rem 0'
                }}
              >
                <CheckCircle sx={{ fontSize: 80, color: '#10b981' }} />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔄 Show loading while checking setup status
  if (setupStatus === null) {
    return (
      <div className="setup-wizard">
        <div className="setup-background" />
        <div className="gradient-overlay" />
        <div className="setup-container">
          <div style={{ textAlign: 'center', color: 'white', padding: '2rem' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
              🔄 Vérification de la configuration...
            </div>
            <div style={{ opacity: 0.7 }}>
              Veuillez patienter
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔄 Use backend API setup status instead of localStorage
  if (setupStatus === true || showLoginForm) {
    // Afficher uniquement le formulaire de connexion
    return (
      <div className="setup-wizard">
        <div className="setup-background" />
        <div className="gradient-overlay" />
        
        <div className="setup-content">
          <div className="setup-container">
            <div className="setup-card">
              <div className="setup-header">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.5 }}
                  className="welcome-logo"
                >
                  <img 
                    src="./images/logo.png" 
                    alt="TravelIT Logo" 
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      objectFit: 'contain' 
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = document.createElement('div');
                      fallback.style.cssText = 'width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #fff;';
                      fallback.textContent = 'T';
                      e.target.parentNode.replaceChild(fallback, e.target);
                    }}
                  />
                </motion.div>
                
                <h1 className="setup-title">Connexion à TravelIT</h1>
                <p className="setup-subtitle">Connectez-vous pour accéder à votre compte</p>
              </div>

              <form onSubmit={handleSubmit} className="setup-form">
                {statusMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`status-message ${statusMessage.type}-message`}
                    style={{
                      backgroundColor: statusMessage.type === 'success' ? 'rgba(34, 197, 94, 0.1)' :
                                      statusMessage.type === 'warning' ? 'rgba(245, 158, 11, 0.1)' :
                                      statusMessage.type === 'info' ? 'rgba(59, 130, 246, 0.1)' :
                                      'rgba(34, 197, 94, 0.1)',
                      border: statusMessage.type === 'success' ? '1px solid rgba(34, 197, 94, 0.2)' :
                              statusMessage.type === 'warning' ? '1px solid rgba(245, 158, 11, 0.2)' :
                              statusMessage.type === 'info' ? '1px solid rgba(59, 130, 246, 0.2)' :
                              '1px solid rgba(34, 197, 94, 0.2)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: statusMessage.type === 'success' ? '#22c55e' :
                             statusMessage.type === 'warning' ? '#f59e0b' :
                             statusMessage.type === 'info' ? '#3b82f6' :
                             '#22c55e'
                    }}
                  >
                    {statusMessage.type === 'success' && <CheckCircle sx={{ fontSize: 16 }} />}
                    {statusMessage.type === 'warning' && <AlertCircle sx={{ fontSize: 16 }} />}
                    {statusMessage.type === 'info' && <Info sx={{ fontSize: 16 }} />}
                    {statusMessage.text}
                  </motion.div>
                )}
                
                {errors.submit && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="error-message"
                  >
                    <AlertCircle sx={{ fontSize: 16 }} />
                    {errors.submit}
                  </motion.div>
                )}

                <div className="input-group">
                  <label>Identifiant de connexion</label>
                  <div className="input-wrapper">
                    <User className="input-icon" sx={{ fontSize: 18 }} />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Nom d'utilisateur créé lors de la configuration"
                      className={errors.username ? 'error' : ''}
                    />
                  </div>
                  {errors.username && (
                    <span className="error-text">{errors.username}</span>
                  )}
                  <div className="help-text" style={{
                    fontSize: '0.85em',
                    color: 'rgba(255, 255, 255, 0.6)',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Shield sx={{ fontSize: 14 }} />
                    Utilisez le nom d'utilisateur défini lors de la configuration initiale
                  </div>
                </div>

                <div className="input-group">
                  <label>Mot de passe</label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" sx={{ fontSize: 18 }} />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Votre mot de passe"
                      className={errors.password ? 'error' : ''}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="primary-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        style={{ marginRight: '8px' }}
                      >
                        <Shield sx={{ fontSize: 18 }} />
                      </motion.div>
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      <LogIn sx={{ fontSize: 18 }} />
                      Se connecter
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </>
                  )}
                </button>
              </form>

              {/* Security Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <Shield sx={{ fontSize: 24, color: '#3b82f6', marginRight: '0.5rem' }} />
                  <h3 style={{ color: '#fff', margin: 0 }}>Connexion Sécurisée</h3>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ fontSize: 16, color: '#10b981', marginRight: '0.5rem' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                      Données cryptées
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ fontSize: 16, color: '#10b981', marginRight: '0.5rem' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                      Accès sécurisé
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ fontSize: 16, color: '#10b981', marginRight: '0.5rem' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                      Session protégée
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Quick Features Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                style={{
                  marginTop: '1.5rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem'
                }}
              >
                <div style={{
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Briefcase sx={{ fontSize: 24, color: '#8b5cf6', marginBottom: '0.5rem' }} />
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', margin: 0 }}>
                    Gestion d'Agence
                  </p>
                </div>
                
                <div style={{
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <FileText sx={{ fontSize: 24, color: '#06b6d4', marginBottom: '0.5rem' }} />
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', margin: 0 }}>
                    Réservations
                  </p>
                </div>
                
                <div style={{
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Shield sx={{ fontSize: 24, color: '#10b981', marginBottom: '0.5rem' }} />
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', margin: 0 }}>
                    Sécurisé
                  </p>
                </div>
              </motion.div>

              {/* Footer Info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                style={{
                  textAlign: 'center',
                  marginTop: '3rem',
                  padding: '1.5rem 1rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                Une fois connecté, vous accéderez au tableau de bord principal de TravelIT
                <br />
                <strong>Système de Gestion pour Agences de Voyage</strong>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no setup completed, redirect to setup
  return (
    <div className="setup-wizard">
      <div className="setup-background" />
      <div className="gradient-overlay" />
      
      <div className="setup-content">
        <div className="setup-container">
          <div className="setup-card">
            <div className="setup-header">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="welcome-logo"
              >
                <img 
                  src="./images/logo.png" 
                  alt="TravelIT Logo" 
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    objectFit: 'contain' 
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.style.cssText = 'width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #fff;';
                    fallback.textContent = 'T';
                    e.target.parentNode.replaceChild(fallback, e.target);
                  }}
                />
              </motion.div>
              
              <h1 className="setup-title">Configuration Requise</h1>
              <p className="setup-subtitle">Vous devez d'abord terminer la configuration initiale</p>
            </div>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="primary-button"
              onClick={() => navigate('/setup')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Shield sx={{ fontSize: 18 }} />
              Aller à la Configuration
              <ChevronRight sx={{ fontSize: 18 }} />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelITLogin;
